<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：ログイン画面
 *
 * 作成者：2021/11/18 ノア）虞
 * 最終修正者：2021/11/19 ノア）虞
 *
 -->
<template>
  <div class="container">
    <div class="content">
      <div class="body">
        <div class="conbox">
          <!-- MFA認証ボックス -->
          <div class="smbox mfa-box" v-if="isMFAValid">
            <div class="form-validbox">
              <div class="qrcode" v-if="!isMFABindFlag">
                <span class="qrcode-scan-tip">{{ $t('mfaValid.qrcodeScanTip') }}</span>
                <img :src="qrcodeBase64" alt="" class="qrcode-base64-img" />
              </div>
              <el-form
                :model="mfaVerifyForm"
                :rules="loginRules"
                ref="mfaVerifyFormRef"
                class="two-valid-form"
                label-position="top"
                @submit.native.prevent
              >
                <el-form-item prop="code" :label="$t('mfaValid.varifyCodeInputTip')" label-width="50px" size="small">
                  <el-input v-model.trim="mfaVerifyForm.code" />
                </el-form-item>

                <el-form-item>
                  <el-button class="two-valid-btn tti-btn-default tti-btn-001" @click="doMFAValid">{{
                    $t('mfaValid.varify')
                  }}</el-button>
                </el-form-item>
              </el-form>
            </div>
          </div>
          <!-- ログインボックス -->
          <div class="smbox" v-if="!isMFAValid">
            <!-- ロゴ -->
            <div class="logo">
              <img src="../../assets/images/logo.png" alt />
            </div>
            <div class="form-itembox">
              <el-form :model="loginForm" :rules="loginRules" ref="loginFormRef">
                <el-form-item prop="userId">
                  <el-input v-model.trim="loginForm.userId">
                    <i slot="prefix" class="el-input__icon el-icon-user"></i>
                  </el-input>
                </el-form-item>
                <el-form-item prop="password">
                  <el-input v-model.trim="loginForm.password" type="password">
                    <i slot="prefix" class="el-input__icon el-icon-lock"></i>
                  </el-input>
                </el-form-item>
                <el-form-item>
                  <el-checkbox v-model="isAgreementChecked">
                    <span class="agreement">
                      <a class="agreementLink" @click.prevent="onAgreementClick">{{ $t('login.agreement') }}</a>
                      <span>{{ $t('login.agreementAgress') }}</span>
                    </span>
                  </el-checkbox>
                </el-form-item>
                <el-form-item>
                  <el-button
                    :disabled="!isAgreementChecked || !canLogin"
                    class="login-btn tti-btn-default tti-btn-001"
                    @click="doLogin"
                    >{{ $t('login.login') }}</el-button
                  >
                </el-form-item>
              </el-form>
              <!-- お知らせ -->
              <span class="notice-header">{{ $t('login.notice') }}</span>
              <div class="notice">
                <div class="notice-item" v-for="(item, index) in announcementList" :key="index">
                  <div class="notice-item-header">
                    <div class="notice-item-title">
                      {{ item.title }}
                      <span
                        class="notice-item-content-view"
                        v-if="visibleContentIndex !== index"
                        @click="toggleContentVisibility(index)"
                      >
                        {{ '...' }}
                      </span>
                    </div>
                    <div class="notice-item-date">{{ item.publicStartDate }}</div>
                  </div>

                  <div class="notice-item-content" v-if="visibleContentIndex === index">
                    {{ item.context }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 著作権情報 -->
        <div class="footer">
          <copyright></copyright>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import * as jsotp from 'jsotp'

import config from '@/config/config'
import manualConfig from '@/config/manualConfig'
import { i18n } from '@/locales'
import { getAnnouncementLoginListData, postLogin, postMFAVerify } from '@/store/apis'
import { common, dialog } from '@/store/modules'
import { LoginParam, MFAVerifyParam, ToolAnnouncementData } from '@/models'
import { clearLocal, clearSession, localService, sessionService } from '@/commons/services/localService'
import { VALID_PATTERN, RULES, validateService } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import Copyright from '@/components/Copyright.vue'
import { getCookie, setCookie } from '@/utils/cookieUtils'
import { AccessToken, MFABindFlag, UserRole } from '@/commons/constants'
import HelperService from '@/service/helperService'
import { clearWindowCookie, isRefresh, saveLoginParam } from '@/service/refreshAndCloseWindowService'

@Component({
  name: 'Login',
  components: { Copyright }
})
export default class Login extends Vue {
  /**
   * ログインフォームのref
   */
  @Ref() readonly loginFormRef!: Form

  /**
   * ログインできるかどうか
   */
  canLogin = true

  /**
   * MFAインターフェースが表示されているかどうか
   */
  isMFAValid = false

  /**
   * qrcode base64 string
   */
  qrcodeBase64 = ''

  /**
   * MFAバインドフラグ
   */
  isMFABindFlag = false

  /**
   * MFA検証のsecret
   */
  mfaSecret = ''

  /**
   * ログイン情報
   */
  loginForm: LoginParam = {
    userId: '',
    password: ''
  }

  /**
   * 利用規約に同意する。
   */
  isAgreementChecked = false

  /**
   * ログイン通知
   */
  announcementList: Array<ToolAnnouncementData> = []

  /**
   * 通知内容表示フラグ
   */
  visibleContentIndex = -1

  /**
   * クッキー
   *
   * @returns {string} クッキーの値
   */
  getAccessToken(): string {
    return getCookie(AccessToken)
  }

  /**
   * ログイン検証ルール
   */
  loginRules: Form['rules'] = {
    userId: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('login.userId')]).toString(),
        trigger: 'blur'
      },
      // 半角英数字および記号
      {
        pattern: VALID_PATTERN[RULES.ALPHANUMERICANDSYMBOLS],
        message: i18n.t('vaildate.common.alphanumericAndSymbols', [i18n.t('login.userId')]).toString(),
        trigger: 'blur'
      },
      // 20文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 20,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('login.userId'), 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    password: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('login.password')]).toString(),
        trigger: 'blur'
      }
    ],
    code: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('mfaValid.code')]).toString(),
        trigger: 'blur'
      },
      // 数字および記号
      {
        pattern: VALID_PATTERN[RULES.NUMERIC],
        message: i18n.t('vaildate.common.numberWithLength', [i18n.t('mfaValid.code'), 6]).toString(),
        trigger: 'blur'
      },
      // 6文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [6, 6],
            message: i18n.t('vaildate.common.numberWithLength', [i18n.t('mfaValid.code'), 6]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  animationFrames: Array<number> = []

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('Login > mounted', this.getAccessToken())
    if (this.checkToken()) {
      return
    }
    if (!isRefresh()) {
      this.$log.debug('Login > performance', isRefresh())
      clearLocal()
      clearSession()
      HelperService.removeAccessToken()
      clearWindowCookie()
    }

    this.removeAccessTokenCookie()
    this.initStoreStatus()
    this.setAnimationFrame()
    this.fetchTableData()
  }

  /**
   * クッキーを削除する
   */
  removeAccessTokenCookie(): void {
    document.cookie = `${AccessToken}=`
  }

  /**
   * Store初期化
   *
   * @returns {void}
   */
  initStoreStatus(): void {
    common.initStatus()
    dialog.initStatus()
  }

  /**
   * 利用規約をクリックした時の処理
   *
   * @returns {void}
   */
  onAgreementClick(): void {
    window.open(manualConfig.route.agreement, '_blank')
  }

  /**
   * ログインを実行する
   *
   * @returns {void}
   */
  doLogin(): void {
    this.loginFormRef.validate((valid: boolean) => {
      if (!valid) return false

      const param = {
        ...this.loginForm,
        password: window.btoa(this.loginForm.password)
      }
      this.doPostLogin(param)
    })
  }

  /**
   * ログイン
   *
   * @param {LoginParam} loginParam ログインのパラメータ
   * @returns {Promise<void>} アクセストークン
   */
  async doPostLogin(loginParam: LoginParam): Promise<void> {
    try {
      const data = await postLogin(loginParam)
      if (!data) {
        return
      }
      saveLoginParam(loginParam)

      if (!data.token) {
        if (data.mfaBindFlag) {
          if (data.qrcodeBase64) {
            this.qrcodeBase64 = data.qrcodeBase64
          }
          if (data.mfaBindFlag === MFABindFlag.MFA_BIND_FLAG) {
            this.isMFABindFlag = true
          } else {
            this.isMFABindFlag = false
          }
          this.isMFAValid = true
          this.mfaSecret = data.mfaSecret
        }
      } else {
        this.setToken(data)
        window.location.reload()
        this.moveToHome()
      }
    } catch (e) {
      Vue.$log.debug('Login > doPostLogin: e=', e)
      throw e
    }
  }

  /**
   * 指定されたトークンに従って、さまざまなシステムのホーム インターフェイスにジャンプします
   *
   */
  moveToHome(): void {
    const userInfo = localService.userInfo
    Vue.$log.debug('Login > moveToHome: role=', userInfo.role)
    if (userInfo && UserRole.TOOL_ADMIN === userInfo.role) {
      this.$router.push(config.toolMenu.companyHome)
    } else if (userInfo) {
      this.$router.push(config.menu.home)
    }
  }

  /**
   * アクセストークンをローカルストレージに保存する
   *
   * @param {any} data ログインのレスポンス定義
   * @returns {void}
   */
  // eslint-disable-next-line
  setToken(data: any): void {
    try {
      const jwt = data.token
      const payloadBase64 = jwt.substring(jwt.indexOf('.') + 1, jwt.lastIndexOf('.'))
      const payloadStr = decodeURIComponent(escape(window.atob(payloadBase64)))
      const payload = JSON.parse(payloadStr)
      const userInfo = JSON.parse(payload.userInfo)
      for (const key in data) {
        if (key !== 'token') {
          userInfo[key] = data[key]
        }
      }

      setCookie(AccessToken, jwt)
      localService.userInfo = userInfo
      localService.loginCheck = true
      sessionService.loginCheck = true
    } catch (e) {
      Vue.$log.debug('Login > setToken: e=', e)
      msgBoxService.error(i18n.t('messages.common.systemError').toString())
      throw e
    }
  }

  /**
   * システムログイン状態を取得する
   *
   * @returns {boolean} ログイン状態
   */
  checkToken(): boolean {
    if (this.getAccessToken()) {
      this.moveToHome()
      return true
    }

    return false
  }

  /**
   * システムログイン状態を取得する
   *
   * @returns {void}
   */
  checkLogin(): void {
    if (!this.getAccessToken() || !this.canLogin) {
      this.setAnimationFrame()
      return
    }

    if (sessionService.loginCheck || localService.loginCheck) {
      this.moveToHome()
      localService.loginCheck = true
      sessionService.loginCheck = true
    }
  }

  /**
   * MFA認証フォームのref
   */
  @Ref() readonly mfaVerifyFormRef!: Form

  /**
   * MFA検証情報
   */
  mfaVerifyForm: MFAVerifyParam = {
    code: ''
  }

  /**
   * 確認を実行する
   *
   * @returns {void}
   */
  doMFAValid(): void {
    this.mfaVerifyFormRef.validate((valid: boolean) => {
      if (!valid) return false

      const param = {
        ...this.mfaVerifyForm,
        userId: this.loginForm.userId
      }
      Vue.$log.debug('Login > doPostMFAVerify: param=', param)
      this.doPostMFAVerify(param)
    })
  }

  /**
   * MFAの検証
   *
   * @param {MFAVerifyParam} mfaVerifyParam  MFAの検証のパラメータ
   * @returns {Promise<void>} アクセストークン
   */
  async doPostMFAVerify(mfaVerifyParam: MFAVerifyParam): Promise<void> {
    const totp = jsotp.TOTP(this.mfaSecret)
    const verify = totp.verify(mfaVerifyParam.code)

    if (!verify) {
      msgBoxService.error(i18n.t('messages.mfaCodeErr').toString())
      return
    }

    try {
      const data = await postMFAVerify(mfaVerifyParam)
      Vue.$log.debug('Login > doPostMFAVerify: data=', data)

      if (!data || !data.token) {
        return
      }

      this.setToken(data)
      window.location.reload()
      this.moveToHome()
    } catch (e) {
      Vue.$log.debug('Login > doPostMFAVerify: e=', e)
      throw e
    }
  }

  setAnimationFrame(): void {
    this.animationFrames.push(window.requestAnimationFrame(this.checkLogin.bind(this)))
  }

  clearAnimationFrame(): void {
    this.animationFrames.forEach((animationFrame: number) => {
      window.cancelAnimationFrame(animationFrame)
    })
  }

  /**
   * インスタンスが破棄された後にフック処理
   *
   * @returns {void}
   */
  destroyed(): void {
    this.clearAnimationFrame()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`Login > fetchTableData`)
      const data = await getAnnouncementLoginListData()
      this.announcementList = data.list as ToolAnnouncementData[]
      return true
    } catch (e) {
      this.announcementList = []
      this.$log.debug(`Login > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * 制御通知内容表示。
   *
   * @param {number} index 表示する内容のインデックス
   */
  toggleContentVisibility(index: number): void {
    this.visibleContentIndex = index
  }
}
</script>

<style scoped lang="scss">
.container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: auto;
  background: rgb(240, 242, 245) url('../../assets/images/bg.png') no-repeat fixed 50% center;
  font-family: 'Meiryo UI', 'Myriad Pro', 'Helvetica Neue', Arial, Helvetica, sans-serif;
}

.body {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.conbox {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.smbox {
  width: 476px;
  height: 467px;
  background-color: #fff;
  margin: 40px 0;
  box-shadow: 0 0 10px #000;
  background-repeat: no-repeat;
  background-position: left;
  background-size: 100%;
  position: relative;

  .logo {
    text-align: center;
    margin-top: 15px;
  }

  .logo img {
    width: 220px;
  }

  .form-itembox {
    padding: 15px 36px 15px 36px;
    box-sizing: border-box;

    .login-btn {
      width: 100%;
      margin: 0;
      height: 42px;
    }
  }

  .form-validbox {
    padding: 0 50px;
    box-sizing: border-box;

    .qrcode {
      display: flex;
      justify-content: center;
      min-height: 100px;
      flex-direction: column;
      align-items: center;

      .qrcode-scan-tip {
        color: black;
        font-size: 14px;
        margin-top: 10px;
      }

      .qrcode-base64-img {
        width: 150px;
        height: 150px;
      }
    }

    .two-valid-btn {
      width: 100%;
      margin: 0;
      height: 42px;
    }

    .two-valid-form {
      padding: 0 15px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }

  .agreement {
    font-size: $FS01;
    cursor: auto;

    .agreementLink {
      color: $C42;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  ::v-deep .el-checkbox__original {
    font-size: $FS01;
  }

  ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
    color: unset !important;
  }

  ::v-deep .el-form-item__label {
    font-size: $FS01;
    line-height: $FS01;
  }
}

.mfa-box {
  height: auto;
  min-height: 320px;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
}

.notice-header {
  font-size: 12px;
  color: #c00;
}

.notice {
  width: 404px;
  height: 100px;
  color: black;
  overflow: auto;
  border: 1px solid #ccc;
}

.notice-item {
  display: flex;
  flex-wrap: wrap;
}

.notice-item-header {
  display: flex;
  background: #f2f2f2;
  border-bottom: 1px solid #d4d4d4;
  width: 404px;
  justify-content: center;
  align-items: center;
}

.notice-item-title {
  width: 210px;
  white-space: normal;
  font-weight: bold;
}

.notice-item-date {
  margin-left: 11px;
  font-weight: bold;
}

.notice-item-content-view {
  width: 20px;
  float: right;
  cursor: pointer;
  font-weight: normal;
  margin-right: 5px;
}
</style>
